'use client';

import React, { useMemo } from 'react';

import { isFeatureEnabled } from '@ravago/shared/next-data-access';
import { FeatureToggle } from '@ravago/shared/page-data/models/config';
import { ProductRequestType, ViewType } from '@ravago/shared/page-data/models/elastic';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Dropdown } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { ShallowLink } from '@ravago/shared/radiance/components/link/components/ShallowLink/ShallowLink';
import { TabProps } from '@ravago/shared/radiance/components/tabs/components/Tab/Tab';
import { Tabs } from '@ravago/shared/radiance/components/tabs/components/Tabs/Tabs';
import { useTranslations } from '@ravago/shared/translations/client';

import { unitSystems } from '../../../../../shared/physical-property-display/mappers/unit-systems.mapper';
import { usePaginationSerializer } from '../../../hooks/use-pagination-serializer';

interface Props {
   searchType: ProductRequestType;
   viewType: ViewType;
   totals: Record<ProductRequestType, number | undefined>;
   features: FeatureToggle[];
   onTabChange?: (tab: 'product' | 'brand' | 'caseStudy') => void;
   switchView?: (view: 'list' | 'grid') => void;
   allowListViewOption?: boolean;
   locked?: boolean;
   allowShowPhyiscalProperties: boolean;
   listViewActive?: boolean;
   unitSystem: 'SI' | 'US';
   onUomSwitch?: (value: 'SI' | 'US') => void;
}

export const ProductPortfolioTabs: React.FC<Props> = ({
   switchView,
   searchType,
   viewType,
   totals,
   allowShowPhyiscalProperties,
   listViewActive = false,
   features,
   onTabChange,
   allowListViewOption = false,
   locked = false,
   unitSystem,
   onUomSwitch
}) => {
   const translate = useTranslations();
   const serializer = usePaginationSerializer();

   const tabs = useMemo(() => {
      const baseTabs: Array<TabProps> = [
         {
            label: `${translate('products-overview-product-tab')}${totals.product !== undefined ? ` (${totals.product})` : ''}`,
            id: 'product-tab'
         },
         {
            label: `${translate('products-overview-brand-tab')}${totals.brand !== undefined ? ` (${totals.brand})` : ''}`,
            id: 'brand-tab'
         }
      ];

      if (isFeatureEnabled(features, 'case-studies')) {
         baseTabs.push({
            label: `${translate('products-overview-case-study-tab')}${totals.caseStudy !== undefined ? ` (${totals.caseStudy})` : ''}`,
            id: 'case-study-tab'
         });
      }

      return baseTabs;
   }, [totals, features]);

   const getTabIndex = () => {
      switch (searchType) {
         case 'product':
            return 0;
         case 'brand':
            return 1;
         case 'caseStudy':
            return 2;
         default:
            return 0;
      }
   };

   const getTabByIndex = (index: number) => {
      switch (index) {
         case 0:
            return 'product';
         case 1:
            return 'brand';
         case 2:
            return 'caseStudy';
         default:
            return 'product';
      }
   };

   const listViewOptionAllowed = useMemo(() => allowListViewOption && searchType === 'product', [allowListViewOption, searchType]);

   const unitSystemOptions = unitSystems(translate);
   const selectedUnitSystemOption = unitSystemOptions.find((option) => option.id === unitSystem);

   return (
      <Box width="100%">
         <div style={{ pointerEvents: locked ? 'none' : 'unset', width: '100%' }}>
            <Tabs
               tabs={tabs}
               activeTab={getTabIndex()}
               onTabChange={(index) => onTabChange?.(getTabByIndex(index))}
               renderItem={(index, children) => {
                  if (index === getTabIndex()) return children;
                  const tab = getTabByIndex(index);
                  const serialised = serializer({ tab });
                  return <ShallowLink href={serialised}>{children}</ShallowLink>;
               }}
            />
         </div>
         {listViewOptionAllowed && (
            <Box gap="xs">
               {allowShowPhyiscalProperties && listViewActive && (
                  <Dropdown
                     width="200px"
                     data-testid="uom-btn"
                     id="table-view-portfolio__dropdown"
                     emptyOption={false}
                     label={translate('product.information.physical-properties.unit-of-measure')}
                     options={unitSystemOptions}
                     value={selectedUnitSystemOption}
                     onChange={(option) => onUomSwitch?.(option?.id as 'SI' | 'US')}
                  />
               )}
               <Box gap="2xs">
                  <IconButton
                     icon="list"
                     backdrop={viewType === 'list'}
                     variant={viewType === 'list' ? 'on-primary' : 'on-light'}
                     onClick={() => (switchView ? switchView('list') : undefined)}
                  />
                  <IconButton
                     icon="grid-2"
                     backdrop={viewType === 'grid'}
                     variant={viewType === 'grid' ? 'on-primary' : 'on-light'}
                     onClick={() => (switchView ? switchView('grid') : undefined)}
                  />
               </Box>
            </Box>
         )}
      </Box>
   );
};
